// The kit's build recipe — the ordered steps that turn the contents list into a
// finished kit, each optionally illustrated by reference photos (ADR-018 phase
// 4). Sits inside the ONE Assembly control alongside KitContentsEditor, for the
// same reason it does: add / remove / reorder over one array, so a second mount
// would be a double-write affordance rather than a second view.
//
// Order is ARRAY POSITION. There is deliberately no "packing sequence" dropdown
// beside this list — the corpus shows step order already carries that fact, and
// a dropdown would compete with the list for the same answer.
//
// There is no per-step time estimate and there must never be one: how long a
// step takes is internal costing, which R8 keeps off this boundary entirely.
//
// A step's photo is NOT the only image affordance in the Assembly flow — the
// finished-kit photo at the top of KittingFields is visible before any step
// exists, which is what keeps this section's copy from promising an uploader the
// customer cannot yet see. Both render through PhotoStrip.
//
// `assembly_instructions` (below this section, in KittingFields) is NOT the same
// thing and is not replaced by it — that box is direction about the kit as a
// whole, this list is direction per step, and both transmit.

import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BUILD_STEPS_FIELD_KEY,
  buildStepIncomplete,
  acceptAttrForRole,
  type AssemblyBuildStep,
  type AssemblyMode,
} from 'shared';

import { RowDisclosure, RowRecap, useCollapsedRows } from './CollapsibleRows.js';
import { MoveDownIcon, MoveUpIcon, RemoveIcon } from './ControlIcons.js';
import { fieldDomId, useFieldDomId, useFieldIdPrefix } from './fieldDomId.js';
import { readBackValue } from './FieldReadBack.js';
import { PhotoStrip, PhotoTile } from './PhotoStrip.js';
import { STEP_PHOTO_ROLE, type AssemblyPhotos } from './useAssemblyPhotos.js';

// Client-side step key. UNLIKE KitContentRow.row_id it does transmit, because it
// is the only thing tying a reference photo to the step it illustrates. Kept to
// the charset the upload route accepts (STEP_ID_RE).
let stepSeq = 0;
function newStepId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  stepSeq += 1;
  return `step-${Date.now()}-${stepSeq}`;
}

const PHOTO_ACCEPT_ATTR = acceptAttrForRole(STEP_PHOTO_ROLE);

type BuildStepsEditorProps = {
  steps: readonly AssemblyBuildStep[];
  onChange: (steps: AssemblyBuildStep[]) => void;
  // Photos live in Asset rows, not on the step, so they are read and written
  // through their own hook and passed in — one fetch for the whole section
  // rather than one per step.
  photos: AssemblyPhotos;
  errors: Record<string, string>;
  // Renders each step's instruction as a `readOnly`, locked-looking answer rather
  // than a dead input. The reorder / remove / add controls are HIDDEN, and each
  // photo slot keeps its photos and drops its picker — an action carries no answer
  // to read back, so there is nothing for a dead control to state. Passed only by
  // the read-only Assembly panel on a submitted order, which asks nothing.
  disabled?: boolean;
  // Building a kit, or reworking stock already held. A kit may be described in
  // the instructions box instead, so its steps are optional; a rework IS its
  // steps — what happens to the stock is the work, and the photo answering
  // "which label do I remove" hangs off one — so the list is required there.
  // The requirement itself is decided by assemblyMissingFieldKeys; this only
  // marks and voices it (R12).
  mode: AssemblyMode;
  // The heading level this list's title renders at. The Assembly panel sits at
  // different depths on its two mounts — under a subsection h4 when editable,
  // directly under the read-only card's h3 when not — so the level is the
  // caller's to say (see AssemblyPanel). Defaults to the editable path's.
  headingLevel?: 'h4' | 'h5';
};

export function BuildStepsEditor({
  steps,
  onChange,
  photos,
  errors,
  disabled,
  mode,
  headingLevel: Heading = 'h5',
}: BuildStepsEditorProps): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);
  const listError = errors[BUILD_STEPS_FIELD_KEY];
  const listDomId = useFieldDomId(BUILD_STEPS_FIELD_KEY);
  const idPrefix = useFieldIdPrefix();
  const isRework = mode === 'rework';
  const isPerUnit = mode === 'per_unit';

  const stepIds = useMemo(() => new Set(steps.map((s) => s.step_id)), [steps]);

  // Photos whose step is gone — the customer removed the step after taking them.
  // They are shown rather than hidden for the reason ArtworkUploader shows its
  // "_other" bucket: a file the customer cannot see is a file they cannot
  // remove, and it still crosses on the submit manifest. (Its step SCOPE does
  // not — the manifest drops a correlation key that resolves to no step.)
  const unattached = useMemo(
    () => photos.stepPhotos.filter((p) => !p.assembly_step_id || !stepIds.has(p.assembly_step_id)),
    [photos.stepPhotos, stepIds],
  );

  function patchStep(stepId: string, patch: Partial<AssemblyBuildStep>): void {
    onChange(steps.map((s) => (s.step_id === stepId ? { ...s, ...patch } : s)));
  }

  const addRef = useRef<HTMLButtonElement>(null);

  // Which steps are folded to their one-line recap. A recipe of twenty steps is
  // twenty full fieldsets, so the list scrolls away from the customer as they
  // write it — the same problem the quote's item list answers by folding an item
  // once the next one is added. View state only: a folded step stays mounted,
  // validated and transmitted exactly as it was.
  const collapse = useCollapsedRows(steps.map((step) => step.step_id));

  function addStep(): void {
    // Adding the next step says the ones before it are written, so they fold away
    // and the new step opens against a short list. A step still missing its
    // instruction stays open — folding it would hide the field being asked for,
    // and the predicate deciding that is the shared one (R12).
    collapse.collapseFinished(steps.filter((s) => !buildStepIncomplete(s)).map((s) => s.step_id));
    onChange([...steps, { step_id: newStepId(), instruction: '' }]);
  }

  // Removing a step leaves its photos alone — they surface in the unattached
  // strip below, where the customer can keep them for another step or remove
  // them deliberately. Destroying uploads as a side effect of an edit is not a
  // decision a save gets to make on their behalf.
  function removeStep(stepId: string): void {
    onChange(steps.filter((s) => s.step_id !== stepId));
    collapse.drop(stepId);
    // The remove control that was just clicked unmounts with its step, and the
    // browser drops focus to <body> — a keyboard or screen-reader customer loses
    // their place in the recipe and has to tab from the top of the section. The
    // add button is mounted whenever a remove button is (the read-only panel hides
    // both), so it is the stable landing spot, and it is where a customer who just
    // removed a step is most likely headed next.
    addRef.current?.focus();
  }

  function moveStep(index: number, delta: -1 | 1): void {
    const target = index + delta;
    if (target < 0 || target >= steps.length) return;
    const next = [...steps];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved!);
    onChange(next);
  }

  return (
    <div
      className={`build-steps${listError ? ' build-steps--error' : ''}`}
      id={listDomId}
    >
      <Heading className="build-steps__title">
        {isPerUnit
          ? t('orderServices.assembly.perUnit.stepsTitle')
          : isRework
            ? t('orderServices.assembly.rework.stepsTitle')
            : t('orderServices.assembly.steps.title')}
        {/* Two halves of one fact: a rework IS its steps, so the list is
            required there; in a build the list is genuinely optional. The
            asterisk drops off a read-only mount, the way every field-level one in
            this panel does — it promises a gate with nothing left to report.
            "(optional)" stays: it describes the question that was asked. */}
        {isRework ? (
          !disabled && <span className="required-marker"> *</span>
        ) : (
          <span className="help"> {t('shipTo.fields.optionalSuffix')}</span>
        )}
      </Heading>
      <p className="help help--flush">
        {isPerUnit
          ? t('orderServices.assembly.perUnit.stepsHelp')
          : isRework
            ? t('orderServices.assembly.rework.stepsHelp')
            : t('orderServices.assembly.steps.help')}
      </p>

      {/* Both prompts name the Add button — "Add at least one step", "or describe
          it all below" — and it is gone on a read-only mount, so an empty recipe
          reads back as the em-dash instead. Same answer the item list gives, for
          the same reason: a list nobody filled in is an answer. */}
      {steps.length === 0 &&
        (disabled ? (
          <p className="value--locked">{readBackValue(undefined)}</p>
        ) : (
          <p className="build-steps__empty">
            {isRework
              ? t('orderServices.assembly.rework.stepsEmpty')
              : t('orderServices.assembly.steps.empty')}
          </p>
        ))}

      {steps.map((step, idx) => {
        const n = idx + 1;
        // The hint this drives says "tell us what happens in this step, or remove
        // it" — both halves are repairs a read-only mount cannot offer, and the
        // outlined step and its aria-invalid promise the same. So a submitted
        // order reports the step it has and flags nothing.
        const incomplete = !disabled && buildStepIncomplete(step);
        const stepPhotos = photos.stepPhotos.filter((p) => p.assembly_step_id === step.step_id);
        const stepPending = photos.pending.filter(
          (p) => p.role === STEP_PHOTO_ROLE && p.step_id === step.step_id,
        );
        // One namespaced base per step, for the reason KitContentsEditor gives:
        // an `assembly` item mounts a second recipe on the same page.
        const stepKey = fieldDomId(idPrefix, `build_steps.${step.step_id}`);
        const hintId = `${stepKey}.hint`;
        const bodyId = `${stepKey}.body`;
        const folded = !disabled && collapse.isCollapsed(step.step_id);
        const photoCount = stepPhotos.length + stepPending.length;
        return (
          <fieldset
            key={step.step_id}
            className={`build-step${incomplete ? ' build-step--incomplete' : ''}${
              folded ? ' build-step--collapsed' : ''
            }`}
          >
            <legend className="build-step__legend">
              {/* No chevron on a read-only mount: nothing is being entered for a
                  fold to get out of the way of, and a folded step would be an
                  answer the reader has to go find. Same reason the reorder and
                  remove cluster goes. */}
              {!disabled && (
                <RowDisclosure
                  collapsed={folded}
                  bodyId={bodyId}
                  label={
                    folded
                      ? t('orderServices.assembly.collapse.expandStepAria', { n })
                      : t('orderServices.assembly.collapse.collapseStepAria', { n })
                  }
                  onToggle={() => collapse.toggle(step.step_id)}
                />
              )}
              {t('orderServices.assembly.steps.stepLegend', { n })}
            </legend>

            {/* What a folded step says about itself: the instruction, then the
                photo count that is the only other thing on it. The unfinished
                flag matters most here — the inline hint it refers to is hidden. */}
            {folded && (
              <RowRecap
                text={
                  step.instruction.trim() || t('orderServices.assembly.collapse.stepNoInstruction')
                }
                meta={
                  photoCount > 0
                    ? t('orderServices.assembly.collapse.photos', { count: photoCount })
                    : undefined
                }
                flag={incomplete ? t('orderServices.assembly.collapse.needsAttention') : undefined}
                bodyId={bodyId}
                editLabel={t('orderServices.assembly.collapse.edit')}
                onExpand={() => collapse.toggle(step.step_id)}
              />
            )}
            {/* The cluster goes rather than greying out: a step's position in the
                recipe is what the numbered legend above already reads back, and
                the controls that would change it answer nothing. */}
            {!disabled && (
              <div className="build-step__actions">
                <button
                  type="button"
                  className="icon-button"
                  aria-label={t('orderServices.assembly.steps.moveUp', { n })}
                  onClick={() => moveStep(idx, -1)}
                  disabled={idx === 0}
                >
                  <MoveUpIcon />
                </button>
                <button
                  type="button"
                  className="icon-button"
                  aria-label={t('orderServices.assembly.steps.moveDown', { n })}
                  onClick={() => moveStep(idx, 1)}
                  disabled={idx === steps.length - 1}
                >
                  <MoveDownIcon />
                </button>
                <button
                  type="button"
                  className="icon-button icon-button--danger"
                  aria-label={t('orderServices.assembly.steps.remove', { n })}
                  onClick={() => removeStep(step.step_id)}
                >
                  <RemoveIcon />
                </button>
              </div>
            )}

            {/* Hidden, never unmounted: the instruction the customer typed and any
                in-flight upload on this step survive a fold, and `hidden` takes the
                subtree out of the tab order and the accessibility tree as well —
                the posture the item list takes for the same reasons. */}
            <div id={bodyId} hidden={folded}>
              <div className="form-row">
                <label htmlFor={`${stepKey}.instruction`}>
                  {t('orderServices.assembly.steps.instructionLabel')}
                  {/* No marker, and no aria-required, once the step can only be read:
                      both promise a gate that has nothing left to report. */}
                  {!disabled && <span className="required-marker"> *</span>}
                </label>
                {/* aria-required, never the native `required`: a half-written step has
                    to survive a mid-flow save like every other order-services field,
                    and native validation would refuse it. The requirement is real and
                    enforced at SUBMIT (buildStepIncomplete), so the visible marker and
                    the attribute both tell the truth. */}
                {/* `readOnly` rather than `disabled` on a submitted order: the step the
                    customer wrote stays focusable and announced in forms mode, and the
                    locked styling is what keeps a field they cannot edit from looking
                    like one they can. */}
                <textarea
                  id={`${stepKey}.instruction`}
                  rows={2}
                  value={disabled ? readBackValue(step.instruction) : step.instruction}
                  {...(disabled
                    ? { readOnly: true, 'aria-readonly': true, className: 'input--locked' }
                    : {
                        placeholder: isPerUnit
                          ? t('orderServices.assembly.perUnit.instructionPlaceholder')
                          : isRework
                            ? t('orderServices.assembly.rework.instructionPlaceholder')
                            : t('orderServices.assembly.steps.instructionPlaceholder'),
                        'aria-required': 'true',
                      })}
                  onChange={(e) => patchStep(step.step_id, { instruction: e.target.value })}
                  aria-invalid={incomplete ? true : undefined}
                  aria-describedby={incomplete ? hintId : undefined}
                />
              </div>

              <PhotoStrip
                inputId={`${stepKey}.photos`}
                accept={PHOTO_ACCEPT_ATTR}
                labels={{
                  label: t('orderServices.assembly.steps.photosLabel'),
                  loading: t('orderServices.assembly.photos.loading'),
                  dismiss: t('orderServices.assembly.photos.dismiss'),
                  remove: (filename) => t('orderServices.assembly.photos.remove', { filename }),
                  uploadProgress: (filename) =>
                    t('orderServices.assembly.photos.uploadProgress', { filename }),
                  takePhoto: t('orderServices.assembly.photos.takePhoto'),
                }}
                assets={stepPhotos}
                pending={stepPending}
                loading={photos.loading}
                onPick={(files) => photos.uploadStep(files, step.step_id)}
                onRemove={(asset) => void photos.remove(asset)}
                onDismiss={photos.dismissPending}
                disabled={disabled || photos.locked}
              />

              {incomplete && (
                <p className="build-step__hint" id={hintId}>
                  {t('orderServices.assembly.steps.incomplete')}
                </p>
              )}
            </div>
          </fieldset>
        );
      })}

      {/* Above the Add button, not below it: a rework with no steps at all has no
          row to carry the warning, so this line is the only marker there — and one
          sitting under the last control is one the customer scrolls past. */}
      {listError && <span className="field-error">{listError}</span>}

      {!disabled && (
        <button type="button" ref={addRef} onClick={addStep}>
          {steps.length === 0
            ? t('orderServices.assembly.steps.addFirst')
            : t('orderServices.assembly.steps.addAnother')}
        </button>
      )}

      {unattached.length > 0 && (
        <div className="build-steps__unattached">
          <p className="help help--flush">{t('orderServices.assembly.steps.unattached')}</p>
          <ul className="photo-list">
            {unattached.map((asset) => (
              <PhotoTile
                key={asset.id}
                asset={asset}
                onRemove={() => void photos.remove(asset)}
                removeLabel={t('orderServices.assembly.photos.remove', {
                  filename: asset.filename,
                })}
                disabled={disabled}
              />
            ))}
          </ul>
        </div>
      )}

      {/* Narrowed to this section's own role: the kit photo's failure renders on
          the kit slot at the top of the form, beside the control that raised it. */}
      {photos.error?.role === STEP_PHOTO_ROLE && (
        <span className="field-error">{photos.error.message}</span>
      )}
    </div>
  );
}
